// Report computations for the console (plan §7): the daily register, late pickups, overrides and
// unknown-adult attempts, staff attendance, and CSV. Pure functions over events from
// GET /api/v1/sites/:site/events, so they are tested without a server.
import { eventLabel, FLAG_LABEL } from "../shared/format.ts";

export interface EventLike {
  seq: number; id: string; type: string; child_id: string | null; adult_id: string | null; staff_id: string; device_id: string;
  device_ts: number; server_ts: number; clock_offset_ms: number; server_flags: string[]; event_hash?: string;
  pre: { claimed_name?: string | null; reason?: string | null; witness_staff_id?: string | null; flags?: string[]; data?: Record<string, unknown> | null; auth_method?: string };
}

export interface ChildLike { id: string; first: string; last: string; room_id?: string | null; status?: string; created_at?: number | null }

/** Resolves ids to display names; unknown ids fall back to something readable. */
export interface Names { child(id: string | null): string; adult(id: string | null): string; staff(id: string | null): string; room(id: string | null | undefined): string }

// ---------- time in the site's zone ----------

/** When the handover happened, on the server's clock: the device time corrected by the offset measured at upload. */
export const eventTime = (e: Pick<EventLike, "device_ts" | "clock_offset_ms">) => e.device_ts + e.clock_offset_ms;

function parts(ts: number, tz: string) {
  const p = Object.fromEntries(new Intl.DateTimeFormat("en-US", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23" })
    .formatToParts(new Date(ts)).map((x) => [x.type, x.value]));
  return { y: +p.year!, m: +p.month!, d: +p.day!, h: +p.hour!, min: +p.minute!, s: +p.second! };
}

/** YYYY-MM-DD of `ts` in `tz`. */
export function localDate(ts: number, tz: string): string {
  const p = parts(ts, tz);
  return `${p.y}-${String(p.m).padStart(2, "0")}-${String(p.d).padStart(2, "0")}`;
}

/** Minutes since local midnight of `ts` in `tz`. */
export const localMinutes = (ts: number, tz: string) => { const p = parts(ts, tz); return p.h * 60 + p.min; };

/** Epoch ms of `date` (YYYY-MM-DD) at `minutes` past local midnight in `tz`, across DST changes. */
export function zonedTime(date: string, tz: string, minutes = 0): number {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  const wall = Date.UTC(y, m - 1, d, 0, minutes);
  const offsetAt = (ts: number) => { const p = parts(ts, tz); return Date.UTC(p.y, p.m - 1, p.d, p.h, p.min, p.s) - Math.floor(ts / 1000) * 1000; };
  let ts = wall - offsetAt(wall);
  ts = wall - offsetAt(ts);
  return ts;
}

export function addDays(date: string, n: number): string {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/** Every date from `from` to `to` inclusive (at most 62). */
export function dateRange(from: string, to: string): string[] {
  const out: string[] = [];
  for (let d = from; d <= to && out.length < 62; d = addDays(d, 1)) out.push(d);
  return out;
}

/** "18:00" → minutes, or null. */
export const parseClock = (hhmm: string | null | undefined) => {
  const m = hhmm?.match(/^(\d{2}):(\d{2})$/);
  return m ? +m[1]! * 60 + +m[2]! : null;
};

export const clockLabel = (ts: number, tz: string) => new Intl.DateTimeFormat("en-US", { timeZone: tz, hour: "numeric", minute: "2-digit" }).format(new Date(ts));

/**
 * The server-time window to fetch for local dates `from`..`to`. It runs two days past the end,
 * because an event captured offline is sealed (server_ts) when the device reconnects.
 */
export const fetchWindow = (from: string, to: string, tz: string) => ({ since: zonedTime(from, tz), until: zonedTime(addDays(to, 3), tz) });

// ---------- people ----------

/** Who handed over or collected: the adult on file, else the name typed at the door. */
export function adultLabel(e: EventLike, names: Names): string {
  if (e.adult_id) return names.adult(e.adult_id);
  if (e.pre.claimed_name) return `${e.pre.claimed_name} (not on file)`;
  return "Unlisted adult";
}

// ---------- daily register ----------

export interface Handover { at: number; by: string; staff: string; seq: number; flags: string[] }
export interface RegisterRow { child_id: string; name: string; room: string; visits: { in: Handover | null; out: Handover | null }[]; absent: boolean; notes: string[] }
export interface RegisterDay { date: string; rows: RegisterRow[] }

/**
 * One register per day: every child, each in/out pair (a mid-day pickup and return is a second
 * pair), who dropped off and collected, and the staff member on duty. Children with no record
 * that day are listed with nothing, as a paper register would. Events are placed by eventTime.
 */
export function dailyRegister(events: EventLike[], children: ChildLike[], names: Names, o: { from: string; to: string; tz: string }): RegisterDay[] {
  const dates = dateRange(o.from, o.to);
  const byDay = new Map<string, EventLike[]>(dates.map((d) => [d, []]));
  for (const e of events) {
    if (!e.child_id) continue;
    byDay.get(localDate(eventTime(e), o.tz))?.push(e);
  }
  const roster = new Map(children.map((c) => [c.id, c]));
  return dates.map((date) => {
    const evs = byDay.get(date)!.sort((a, b) => eventTime(a) - eventTime(b) || a.seq - b.seq);
    const rows = new Map<string, RegisterRow>();
    const row = (id: string) => {
      let r = rows.get(id);
      if (!r) {
        const c = roster.get(id);
        r = { child_id: id, name: c ? `${c.last}, ${c.first}` : names.child(id), room: names.room(c?.room_id), visits: [], absent: false, notes: [] };
        rows.set(id, r);
      }
      return r;
    };
    // Enrolled children are listed even with no record that day, but not before they were added.
    const dayEnd = zonedTime(addDays(date, 1), o.tz);
    for (const c of children) if (c.status !== "withdrawn" && (c.created_at == null || c.created_at < dayEnd)) row(c.id);
    for (const e of evs) {
      const r = row(e.child_id!);
      const h: Handover = { at: eventTime(e), by: adultLabel(e, names), staff: names.staff(e.staff_id), seq: e.seq, flags: e.server_flags };
      if (e.type === "dropoff") r.visits.push({ in: h, out: null });
      else if (e.type === "pickup") {
        const open = [...r.visits].reverse().find((v) => v.in && !v.out);
        if (open) open.out = h; else r.visits.push({ in: null, out: h });
      } else if (e.type === "absence") {
        r.absent = true;
        const why = typeof e.pre.data?.reason === "string" ? `: ${e.pre.data.reason}` : "";
        r.notes.push(`Absent${why}`);
      } else if (e.type === "override") r.notes.push(`Override at ${clockLabel(h.at, o.tz)}${e.pre.reason ? `: ${e.pre.reason}` : ""}`);
      else if (e.type === "transfer") r.notes.push(`Transferred at ${clockLabel(h.at, o.tz)}${typeof e.pre.data?.to === "string" ? ` to ${e.pre.data.to}` : ""}`);
      else if (e.type === "correction") r.notes.push(`Correction at ${clockLabel(h.at, o.tz)}${e.pre.reason ? `: ${e.pre.reason}` : ""}`);
      else if (e.type === "note" && typeof e.pre.data?.text === "string") r.notes.push(`Note: ${e.pre.data.text}`);
    }
    return { date, rows: [...rows.values()].sort((a, b) => a.name.localeCompare(b.name)) };
  });
}

// ---------- late pickups ----------

export interface LateRow { date: string; child_id: string; name: string; room: string; at: number | null; minutes_late: number | null; by: string | null; staff: string | null }

/**
 * Pickups after the site's closing time, and children dropped off with no pickup recorded once
 * the day has closed (`at` null). Days that have not reached closing time yet are not judged.
 */
export function latePickups(days: RegisterDay[], closing: string | null, tz: string, now = Date.now()): LateRow[] {
  const close = parseClock(closing);
  if (close == null) return [];
  const out: LateRow[] = [];
  for (const day of days) {
    const closedAt = zonedTime(day.date, tz, close);
    for (const r of day.rows) {
      for (const v of r.visits) {
        if (v.out) {
          const late = Math.floor((v.out.at - closedAt) / 60_000);
          if (late > 0) out.push({ date: day.date, child_id: r.child_id, name: r.name, room: r.room, at: v.out.at, minutes_late: late, by: v.out.by, staff: v.out.staff });
        } else if (v.in && now > closedAt) {
          out.push({ date: day.date, child_id: r.child_id, name: r.name, room: r.room, at: null, minutes_late: null, by: null, staff: null });
        }
      }
    }
  }
  return out;
}

// ---------- overrides and unknown or unauthorised adults ----------

export const EXCEPTION_FLAGS = ["unknown_adult", "unauthorised_adult", "restriction_hit", "duplicate_pickup", "duplicate_dropoff", "override", "walk_in_not_allowed", "home_alone_not_allowed", "sibling_not_allowed"];

export interface ExceptionRow { seq: number; id: string; at: number; date: string; type: string; child: string; adult: string; flags: string[]; reason: string; staff: string; witness: string }

/** Overrides and every event the server flagged (unlisted or unauthorised adult, restriction, second pickup). */
export function exceptions(events: EventLike[], names: Names, o: { from: string; to: string; tz: string }): ExceptionRow[] {
  return events
    .filter((e) => e.type === "override" || e.server_flags.some((f) => EXCEPTION_FLAGS.includes(f)) || (e.pre.flags ?? []).includes("unlisted_adult"))
    .map((e) => ({ e, at: eventTime(e) }))
    .filter(({ at }) => { const d = localDate(at, o.tz); return d >= o.from && d <= o.to; })
    .sort((a, b) => a.at - b.at)
    .map(({ e, at }) => ({
      seq: e.seq, id: e.id, at, date: localDate(at, o.tz), type: eventLabel(e.type), child: e.child_id ? names.child(e.child_id) : "",
      adult: adultLabel(e, names), flags: [...new Set([...e.server_flags, ...(e.type === "override" ? ["override"] : [])])].map((f) => FLAG_LABEL[f] ?? f),
      reason: e.pre.reason ?? "", staff: names.staff(e.staff_id), witness: e.pre.witness_staff_id ? names.staff(e.pre.witness_staff_id) : "",
    }));
}

// ---------- staff attendance ----------

export interface ShiftRow { date: string; staff_id: string; staff: string; start: number | null; end: number | null; minutes: number | null }

/** Shift check-ins (shift_start/shift_end) paired per staff member per day. */
export function staffAttendance(events: EventLike[], names: Names, o: { from: string; to: string; tz: string }): ShiftRow[] {
  const shifts = events.filter((e) => e.type === "shift_start" || e.type === "shift_end").map((e) => ({ e, at: eventTime(e) })).sort((a, b) => a.at - b.at);
  const open = new Map<string, ShiftRow>();
  const out: ShiftRow[] = [];
  for (const { e, at } of shifts) {
    const date = localDate(at, o.tz);
    if (e.type === "shift_start") {
      const row: ShiftRow = { date, staff_id: e.staff_id, staff: names.staff(e.staff_id), start: at, end: null, minutes: null };
      open.set(e.staff_id, row);
      out.push(row);
    } else {
      const row = open.get(e.staff_id);
      if (row && row.date === date) { row.end = at; row.minutes = Math.round((at - row.start!) / 60_000); open.delete(e.staff_id); }
      else out.push({ date, staff_id: e.staff_id, staff: names.staff(e.staff_id), start: null, end: at, minutes: null });
    }
  }
  return out.filter((r) => r.date >= o.from && r.date <= o.to).sort((a, b) => a.date.localeCompare(b.date) || a.staff.localeCompare(b.staff) || (a.start ?? a.end ?? 0) - (b.start ?? b.end ?? 0));
}

// ---------- CSV ----------

/**
 * RFC 4180 CSV with CRLF line ends. Cells that a spreadsheet would run as a formula (starting
 * with = + - @ or a tab or carriage return, and not a plain number) get a leading apostrophe.
 */
export function toCsv(rows: (string | number | null | undefined)[][]): string {
  const cell = (v: string | number | null | undefined) => {
    let s = v == null ? "" : String(v);
    if (/^[=+\-@\t\r]/.test(s) && !/^[+-]?\d+(\.\d+)?$/.test(s)) s = `'${s}`;
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return rows.map((r) => r.map(cell).join(",")).join("\r\n") + "\r\n";
}

const t = (ts: number | null | undefined, tz: string) => (ts == null ? "" : clockLabel(ts, tz));

export function registerCsv(days: RegisterDay[], tz: string): string {
  const rows: (string | number | null)[][] = [["Date", "Child", "Room", "In", "Dropped off by", "Staff (in)", "Out", "Collected by", "Staff (out)", "Absent", "Notes"]];
  for (const d of days) for (const r of d.rows) {
    const visits = r.visits.length ? r.visits : [{ in: null, out: null }];
    visits.forEach((v, i) => rows.push([d.date, r.name, r.room, t(v.in?.at, tz), v.in?.by ?? "", v.in?.staff ?? "", t(v.out?.at, tz), v.out?.by ?? "", v.out?.staff ?? "", r.absent ? "yes" : "", i === 0 ? r.notes.join("; ") : ""]));
  }
  return toCsv(rows);
}

export const lateCsv = (rows: LateRow[], tz: string) => toCsv([["Date", "Child", "Room", "Picked up", "Minutes late", "Collected by", "Staff"], ...rows.map((r) => [r.date, r.name, r.room, r.at ? t(r.at, tz) : "No pickup recorded", r.minutes_late, r.by, r.staff])]);

export const exceptionsCsv = (rows: ExceptionRow[], tz: string) => toCsv([["Date", "Time", "Record", "Type", "Child", "Adult", "Flags", "Reason", "Staff", "Witness"], ...rows.map((r) => [r.date, t(r.at, tz), r.seq, r.type, r.child, r.adult, r.flags.join("; "), r.reason, r.staff, r.witness])]);

export const staffCsv = (rows: ShiftRow[], tz: string) => toCsv([["Date", "Staff", "Start", "End", "Minutes"], ...rows.map((r) => [r.date, r.staff, t(r.start, tz), t(r.end, tz), r.minutes])]);
